<template>
  <v-navigation-drawer
    :value="isAddNewUserSidebarActive"
    temporary
    touchless
    :right="!$vuetify.rtl"
    :width="$vuetify.breakpoint.smAndUp ? 350 : '100%'"
    app
    @input="val => $emit('update:is-add-new-user-sidebar-active', val)"
  >
    <v-card height="100%">
      <div class="drawer-header d-flex align-center mb-4">
        <span class="font-weight-semibold text-base text--primary">Adicionar Usuário</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          small
          @click="$emit('update:is-add-new-user-sidebar-active', false)"
        >
          <v-icon size="22">
            {{ icons.mdiClose }}
          </v-icon>
        </v-btn>
      </div>

      <v-card-text>
        <v-form
          ref="formRef"
          @submit.prevent="onSubmit"
        >
          <v-text-field
            v-model="userData.nome"
            class="mb-6"
            dense
            outlined
            label="Nome"
            placeholder="Nome"
            hide-details="auto"
            :rules="[validators.required, validators.nameValidator]"
          />

          <v-text-field
            v-model="userData.sobrenome"
            class="mb-6"
            dense
            outlined
            label="Sobrenome"
            placeholder="Sobrenome"
            hide-details="auto"
            :rules="[validators.required, validators.nameValidator]"
          />

          <v-text-field
            v-model="userData.cpf"
            v-mask="'###.###.###-##'"
            class="mb-6"
            dense
            outlined
            label="CPF"
            placeholder="CPF"
            hide-details="auto"
            :rules="[validators.isCpfValido]"
          />

          <v-text-field
            v-model="userData.login"
            class="mb-6"
            dense
            outlined
            type="email"
            label="Login"
            placeholder="Login"
            hide-details="auto"
            :rules="[validators.required, validators.emailValidator]"
          />

          <v-text-field
            v-model="userData.senha"
            class="mb-6"
            dense
            outlined
            label="Senha"
            placeholder="Senha"
            hide-details="auto"
            :type="isPassword ? 'text' : 'password'"
            :append-icon="!isPassword ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
            :rules="[validators.required, validators.passwordValidator]"
            @click:append="isPassword = !isPassword"
          />

          <v-select
            v-model="userData.telefones[0].tipoId"
            class="mb-6"
            dense
            outlined
            label="Tipo"
            hide-details
            item-text="title"
            item-value="value"
            :items="tiposDeNumeros"
            :rules="[validators.select]"
          />

          <v-text-field
            v-if="userData.telefones[0].tipoId === 2"
            v-model="userData.telefones[0].numero"
            v-mask="'(##) ####-####'"
            outlined
            dense
            label="Número telefone"
            placeholder="Número telefone"
            hide-details="auto"
            class="mb-6"
            :rules="[validators.isTelefoneFixoValido]"
          />

          <v-text-field
            v-else
            v-model="userData.telefones[0].numero"
            v-mask="'(##) #####-####'"
            class="mb-6"
            dense
            outlined
            label="Número celular"
            placeholder="Número celular"
            hide-details="auto"
            :rules="[validators.isTelefoneCelularValido]"
          />

          <v-row
            v-if="userData.telefones[0].tipoId !== 2"
            cols="12"
            md="2"
            class="mb-3"
          >
          </v-row>

          <v-select
            v-model="userData.departamentoId"
            class="mb-6"
            dense
            outlined
            label="Departamentos"
            placeholder="Departamentos"
            hide-details
            item-text="nome"
            item-value="id"
            :items="departamentos"
            :rules="[validators.select]"
          />

          <v-select
            v-model="userData.cargoId"
            class="mb-6"
            dense
            outlined
            label="Cargos"
            placeholder="Cargos"
            hide-details
            item-text="nome"
            item-value="id"
            :items="cargos"
            :rules="[validators.select]"
          />

          <v-select
            v-model="userData.perfilId"
            class="mb-6"
            dense
            outlined
            label="Perfis"
            placeholder="Perfis"
            hide-details
            item-text="nome"
            item-value="id"
            :items="perfis"
            :rules="[validators.select]"
          />
          <v-select
            v-model="userData.atuacaoId"
            class="mb-6"
            dense
            outlined
            label="Atuação"
            hide-details
            item-text="title"
            item-value="value"
            :items="tipoAtuacoes"
            :rules="[validators.select]"
          />

          <v-btn
            color="primary"
            type="submit"
            class="me-3 mb-1"
          >
            <v-progress-circular
              v-if="loading"
              class="mx-6"
              indeterminate
              color="secundary"
            />

            <span v-else>Adicionar</span>
          </v-btn>

          <v-btn
            color="secondary"
            outlined
            type="reset"
            class="mb-1"
            @click="resetUserData"
          >
            Cancelar
          </v-btn>
        </v-form>

        <v-alert
          v-if="msgsSuccess"
          dense
          dismissible
          type="success"
          class="mt-4"
        >
          Cadastrado com sucesso!
        </v-alert>

        <v-alert
          v-if="msgsError"
          shaped
          outlined
          type="error"
          class="mt-4"
        >
          {{ errorMsg }}
        </v-alert>
      </v-card-text>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import Vue from 'vue'
import { ref, watch } from '@vue/composition-api'
import { mdiClose, mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'
import { nameValidator, emailValidator, passwordValidator, required, select } from '@core/utils/validation'
import { isTelefoneCelularValido, isTelefoneFixoValido, isCpfValido } from '@/utils/validation'
import CargoService from '@/services/modules/CargoService'
import ColaboradorService from '@/services/modules/ColaboradorService'
import DepartamentoService from '@/services/modules/DepartamentoService'
import PerfilService from '@/services/modules/PerfilService'
import useForm from '@/composables/useForm'

export default {
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-user-sidebar-active',
  },

  props: {
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
  },

  setup(props, { emit }) {
    const blankUserData = {
      nome: '',
      sobrenome: '',
      cpf: '',
      login: '',
      senha: '',
      empresaId: null,
      departamentoId: null,
      cargoId: null,
      perfilId: null,
      atuacaoId: null,
      telefones: [
        {
          tipoId: null,
          numero: '',
          ramal: '',
          tipoContato: '',
        },
      ],
    }

    const { formRef, loading, FORM_ERROR, resetForm, validateForm } = useForm()

    const tiposDeNumeros = [
      { title: 'Celular', value: 1 },
      { title: 'Fixo', value: 2 },
    ]

    const tipoAtuacoes = [
      { title: 'Gestor', value: 1 },
      { title: 'Agentes', value: 2 },
    ]
    const departamentos = ref([])
    const cargos = ref([])
    const perfis = ref([])
    const atuacoes = ref([])

    const isPassword = ref(false)
    const msgsSuccess = ref(false)
    const msgsError = ref(false)

    async function getDepartamentos() {
      departamentos.value = await DepartamentoService.get()
    }

    async function getCargos() {
      cargos.value = await CargoService.get()
    }
    async function getPerfis() {
      perfis.value = await PerfilService.get()
    }

    const userData = ref(JSON.parse(JSON.stringify(blankUserData)))
    const errorMsg = ref('')

    function resetUserData() {
      msgsError.value = false
      userData.value = JSON.parse(JSON.stringify(blankUserData))
      resetForm()

      setTimeout(() => {
        emit('update:is-add-new-user-sidebar-active', false)
      }, 2000)
    }

    async function onSubmit() {
      if (loading.value) return

      if (!validateForm()) {
        Vue.$toast.warning(FORM_ERROR)
        return
      }

      try {
        loading.value = true

        const dataToSend = { ...userData.value }
        dataToSend.departamentoId = dataToSend.departamentoId || null
        dataToSend.cargoId = dataToSend.cargoId || null
        dataToSend.perfilId = dataToSend.perfilId || null
        dataToSend.atuacaoId = dataToSend.atuacaoId || null
        dataToSend.telefones[0].ramal = dataToSend.telefones[0].ramal || null
        dataToSend.telefones[0].tipoContato = dataToSend.telefones[0].tipoContato || null

        await ColaboradorService.criar(dataToSend)

        msgsSuccess.value = !msgsSuccess.value

        setTimeout(() => {
          msgsSuccess.value = !msgsSuccess.value
        }, 6000)

        emit('refetch-data')
        resetUserData()
      } finally {
        loading.value = false
      }
    }

    watch(
      () => props.isAddNewUserSidebarActive,
      () => {
        getCargos()
        getDepartamentos()
        getPerfis()
      },
    )

    return {
      formRef,
      loading,

      tiposDeNumeros,
      tipoAtuacoes,
      departamentos,
      cargos,
      perfis,
      atuacoes,

      resetUserData,
      onSubmit,
      userData,
      msgsSuccess,
      msgsError,
      errorMsg,
      isPassword,

      validators: {
        required,
        emailValidator,
        select,
        nameValidator,
        isCpfValido,
        passwordValidator,
        isTelefoneCelularValido,
        isTelefoneFixoValido,
      },

      icons: {
        mdiClose,
        mdiEyeOffOutline,
        mdiEyeOutline,
      },
    }
  },
}
</script>
